import * as i0 from '@angular/core';
import { Injectable, InjectionToken, NgModule, EventEmitter, Component, ChangeDetectionStrategy, Input, Output, ViewEncapsulation, Inject } from '@angular/core';
import { map, findIndex, find, uniq, sortBy, filter as filter$1, flatten } from 'lodash';
import * as i1 from '@play/common';
import { ExpansionState, CommonsLoadingModule, CommonsComponentModule, CommonsDateModule, SvgIconsModule, CommonsModule } from '@play/common';
import * as i2 from '@play/translate';
import { TranslationModule } from '@play/translate';
import * as i7 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2$1 from '@ngrx/store';
import { LoadPubgMatchMetaDataOnceAction, getPubgMatchMetaData, exists, getUser, LoadLeagueOnceAction, getLeague, entryAvailable, distinct, LoadContestantsOnceAction, getContestants, LoadLeagueResultsAction, getLeagueResults, getTeamMembersMatches, LoadMatchOnceAction, getMatch, LoadPlayersOnceAction, getPlayer, LoadTeamsOnceAction, getTeam, LoadRosterOnceAction, LoadMembersOnceAction, getTeamMembers, LoadMembersMatchesOnceAction, getRoster, LoadFortniteMatchMetaDataOnceAction, getFortniteMatchMetaData, LoadPubgmMatchMetaDataOnceAction, getPubgmMatchMetaData, StateModule } from '@play/state';
import { of, combineLatest, BehaviorSubject, Subject, forkJoin } from 'rxjs';
import { filter, map as map$1, tap, switchMap, startWith, first, mergeMap, shareReplay, take, distinctUntilChanged } from 'rxjs/operators';
import * as i2$2 from '@play/ranking';
import { RankingComponentsModule, RankingModule } from '@play/ranking';
import * as i6 from '@play/api-client';
import { isTeam, hasIntegration, isPlayer } from '@play/utility/league';

class PubgGroupService {
    /**
     * Combines the meta data of multiple matches
     * Resulting meta data should not be used to display the rank since this will be incorrect
     *
     * @param matchMetaData
     */
    combineGroupMetaData(matchMetaData) {
        const contestantGroups = matchMetaData.reduce((data, meta) => {
            meta.contestants.forEach(contestant => {
                if (!data[contestant.id]) {
                    data[contestant.id] = Object.assign(Object.assign({}, contestant), { players: [] });
                }
                data[contestant.id].players = [...data[contestant.id].players, ...contestant.players];
            });
            return data;
        }, {});
        const contestantArray = map(contestantGroups, group => group);
        return contestantArray.reduce((result, contestant) => {
            result.contestants.push(this.combinePubgPlayers(contestant));
            return result;
        }, { contestants: [] });
    }
    /**
     * Calculates duplicate players in to one object
     *
     * @param contestant
     */
    combinePubgPlayers(contestant) {
        return contestant.players.reduce((data, player) => {
            const index = findIndex(data.players, p => p.id === player.id);
            if (index === -1) {
                return Object.assign(Object.assign({}, data), { players: [...data.players, player] });
            }
            data.players[index] = Object.assign(Object.assign({}, data.players[index]), { kills: data.players[index].kills + player.kills });
            return data;
        }, Object.assign(Object.assign({}, contestant), { players: [] }));
    }
}
PubgGroupService.ɵfac = function PubgGroupService_Factory(t) { return new (t || PubgGroupService)(); };
PubgGroupService.ɵprov = i0.ɵɵdefineInjectable({ token: PubgGroupService, factory: PubgGroupService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PubgGroupService, [{
        type: Injectable
    }], null, null); })();

const RANKING_MEMBER_TEXTS = new InjectionToken("ffaranking.ranking-member-texts");
/**
 * This is the base service for adding an appendix to a roster's member.
 * The appendix is shown underneath the member's name.
 *
 * You can add as many GI specific services as you want by adding a provider to RANKING_MEMBER_TEXTS
 * and overriding `public readonly integration`
 */
class RankingMemberTextService {
    constructor(linkService, translateService) {
        this.linkService = linkService;
        this.translateService = translateService;
        this.integration = null;
    }
    /**
     * @param contestant
     * @param metaData
     */
    getRankingMember(contestant, metaData) {
        return Object.assign(Object.assign({}, contestant), { appendix: "" });
    }
}
RankingMemberTextService.ɵfac = function RankingMemberTextService_Factory(t) { return new (t || RankingMemberTextService)(i0.ɵɵinject(i1.LinkService), i0.ɵɵinject(i2.TranslateService)); };
RankingMemberTextService.ɵprov = i0.ɵɵdefineInjectable({ token: RankingMemberTextService, factory: RankingMemberTextService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RankingMemberTextService, [{
        type: Injectable
    }], function () { return [{ type: i1.LinkService }, { type: i2.TranslateService }]; }, null); })();

class PubgRankingMemberTextService extends RankingMemberTextService {
    constructor() {
        super(...arguments);
        this.integration = "pubg";
    }
    /**
     * Adds the kills of the match metadata to the appendix
     *
     * @param contestant
     * @param metaData
     */
    getRankingMember(contestant, metaData) {
        let text;
        const kills = metaData.contestants.reduce((killCount, con) => {
            const player = find(con.players, p => p.id === contestant.id);
            if (player) {
                return player.kills;
            }
            return killCount;
        }, null);
        if (kills === null) {
            text = this.translateService.translate("ffa-ranking:member-kills-na", {});
        }
        else {
            text = this.translateService.translate("ffa-ranking:member-kills", {
                kills: kills,
            });
        }
        return Object.assign(Object.assign({}, contestant), { appendix: text });
    }
}
PubgRankingMemberTextService.ɵfac = function PubgRankingMemberTextService_Factory(t) { return ɵPubgRankingMemberTextService_BaseFactory(t || PubgRankingMemberTextService); };
PubgRankingMemberTextService.ɵprov = i0.ɵɵdefineInjectable({ token: PubgRankingMemberTextService, factory: PubgRankingMemberTextService.ɵfac });
const ɵPubgRankingMemberTextService_BaseFactory = /*@__PURE__*/ i0.ɵɵgetInheritedFactory(PubgRankingMemberTextService);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PubgRankingMemberTextService, [{
        type: Injectable
    }], null, null); })();

const MATCH_METADATA = new InjectionToken("ffaranking.match-metadata");
/**
 * This service is responsible for loading the matches meta data of an FFA group.
 * The metadata is for example used by the PUBG integration to display kills underneath a members name.
 *
 * You can add as many GI specific services as you want by adding a provider to MATCH_METADATA
 * and overriding `public readonly integration`
 */
class MatchMetadataService {
    constructor() {
        this.integration = null;
    }
    getMetaData(matches) {
        return of(null);
    }
}
MatchMetadataService.ɵfac = function MatchMetadataService_Factory(t) { return new (t || MatchMetadataService)(); };
MatchMetadataService.ɵprov = i0.ɵɵdefineInjectable({ token: MatchMetadataService, factory: MatchMetadataService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MatchMetadataService, [{
        type: Injectable
    }], null, null); })();

class PubgMatchMetadataService extends MatchMetadataService {
    constructor(groupService, store) {
        super();
        this.groupService = groupService;
        this.store = store;
        this.integration = "pubg";
    }
    /**
     * Fetch the metadata of all the matches in a group and combine them as 1 so we can use them
     * to display kills.
     *
     * @param matches
     */
    getMetaData(matches) {
        const metaData$ = matches.map(result => {
            this.store.dispatch(new LoadPubgMatchMetaDataOnceAction(result.id));
            return this.store.select(getPubgMatchMetaData(result.id)).pipe(filter(exists));
        });
        return combineLatest(metaData$).pipe(map$1(metaData => {
            const reducedMetaData = metaData.reduce((all, current) => (current.entry ? [...all, current.entry] : all), []);
            return this.groupService.combineGroupMetaData(reducedMetaData);
        }));
    }
}
PubgMatchMetadataService.ɵfac = function PubgMatchMetadataService_Factory(t) { return new (t || PubgMatchMetadataService)(i0.ɵɵinject(PubgGroupService), i0.ɵɵinject(i2$1.Store)); };
PubgMatchMetadataService.ɵprov = i0.ɵɵdefineInjectable({ token: PubgMatchMetadataService, factory: PubgMatchMetadataService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PubgMatchMetadataService, [{
        type: Injectable
    }], function () { return [{ type: PubgGroupService }, { type: i2$1.Store }]; }, null); })();

class PubgFFARankingModule {
}
PubgFFARankingModule.ɵfac = function PubgFFARankingModule_Factory(t) { return new (t || PubgFFARankingModule)(); };
PubgFFARankingModule.ɵmod = i0.ɵɵdefineNgModule({ type: PubgFFARankingModule });
PubgFFARankingModule.ɵinj = i0.ɵɵdefineInjector({ providers: [
        PubgGroupService,
        PubgMatchMetadataService,
        PubgRankingMemberTextService,
        {
            provide: MATCH_METADATA,
            useExisting: PubgMatchMetadataService,
            multi: true,
        },
        {
            provide: RANKING_MEMBER_TEXTS,
            useExisting: PubgRankingMemberTextService,
            multi: true,
        },
    ], imports: [[CommonModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(PubgFFARankingModule, { imports: [CommonModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PubgFFARankingModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule],
                providers: [
                    PubgGroupService,
                    PubgMatchMetadataService,
                    PubgRankingMemberTextService,
                    {
                        provide: MATCH_METADATA,
                        useExisting: PubgMatchMetadataService,
                        multi: true,
                    },
                    {
                        provide: RANKING_MEMBER_TEXTS,
                        useExisting: PubgRankingMemberTextService,
                        multi: true,
                    },
                ],
            }]
    }], null, null); })();

class RankingGroupService {
    /**
     * @param results
     */
    getGroupsFromResults(results) {
        const rounds = uniq(results.map(result => result.round));
        const obj = {};
        rounds.forEach(round => {
            const roundMatches = results.filter(result => result.round === round);
            const groups = uniq(roundMatches.map(result => result.group));
            obj[round] = sortBy(groups, group => group);
        });
        return obj;
    }
    /**
     * @param results
     */
    getContestantGroupsFromResults(results, contestantId) {
        const matches = results.filter(result => result.participants.filter(participant => participant.id === contestantId).length > 0);
        return matches.map(match => ({
            round: match.round,
            group: match.group,
        }));
    }
    /**
     * Returns the correctly sorted matches of a group
     * Sorts them by position ascending
     *
     * @param results
     * @param round
     * @param group
     */
    getGroupMatchesFromResults(results, group) {
        const matches = filter$1(results, result => {
            return result.round === group.round && result.group === group.group;
        });
        return sortBy(matches, match => match.position);
    }
}
RankingGroupService.ɵfac = function RankingGroupService_Factory(t) { return new (t || RankingGroupService)(); };
RankingGroupService.ɵprov = i0.ɵɵdefineInjectable({ token: RankingGroupService, factory: RankingGroupService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RankingGroupService, [{
        type: Injectable
    }], null, null); })();

class RankingRoundService {
    /**
     * @param results
     */
    getRoundsFromResults(results) {
        const rounds = uniq(results.map(result => result.round));
        return sortBy(rounds, round => round);
    }
}
RankingRoundService.ɵfac = function RankingRoundService_Factory(t) { return new (t || RankingRoundService)(); };
RankingRoundService.ɵprov = i0.ɵɵdefineInjectable({ token: RankingRoundService, factory: RankingRoundService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RankingRoundService, [{
        type: Injectable
    }], null, null); })();

class RankingContestantService {
    constructor(linkService, translateService) {
        this.linkService = linkService;
        this.translateService = translateService;
    }
    /**
     * @param team
     */
    getRankingContestantFromTeam(rosterEnabled, leagueUrl, id, team) {
        if (!team) {
            return {
                id: id,
                image: null,
                name: null,
                country: null,
                hasPremium: null,
                hasTrusted: null,
                link: null,
                homepage: null,
                gameaccount: null,
            };
        }
        return {
            id: id,
            image: team.logo,
            name: team.name,
            country: team.region,
            hasPremium: team.premium,
            hasTrusted: team.trustLevel > 0,
            link: this.linkService.teamContestant(leagueUrl, rosterEnabled, id),
            homepage: team.homepage,
            gameaccount: null,
        };
    }
    getRankedContestant(contestant, rankings, gameIntegration) {
        let rank = 0;
        let points = 0;
        let tiebreaker = 0;
        const ranks = find(rankings ? rankings.ranking : [], ranking => ranking.id === contestant.id);
        if (ranks) {
            rank = ranks.place;
            points = ranks.points.reduce((sum, point) => {
                return sum + point;
            }, 0);
            tiebreaker = (gameIntegration || "").includes("gameintegrationapexlegends")
                ? ranks.tiebreaker
                : ranks.tiebreaker[0] || 0;
        }
        return Object.assign(Object.assign({}, contestant), { rank: rank, points: points, tiebreaker: tiebreaker });
    }
    /**
     * @param player
     */
    getRankingContestantFromPlayer(id, player, gameaccount) {
        if (!player) {
            return {
                id: id,
                image: null,
                name: null,
                country: null,
                hasPremium: null,
                hasTrusted: null,
                link: null,
                homepage: null,
                gameaccount: null,
            };
        }
        return {
            id: id,
            image: player.photo,
            name: player.nickname,
            country: player.region,
            hasPremium: player.premium,
            hasTrusted: player.trustLevel > 0,
            link: this.linkService.player(id, player.alias),
            homepage: player.homepage,
            gameaccount: gameaccount,
        };
    }
}
RankingContestantService.ɵfac = function RankingContestantService_Factory(t) { return new (t || RankingContestantService)(i0.ɵɵinject(i1.LinkService), i0.ɵɵinject(i2.TranslateService)); };
RankingContestantService.ɵprov = i0.ɵɵdefineInjectable({ token: RankingContestantService, factory: RankingContestantService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RankingContestantService, [{
        type: Injectable
    }], function () { return [{ type: i1.LinkService }, { type: i2.TranslateService }]; }, null); })();

var RankingFFAPickerSteps;
(function (RankingFFAPickerSteps) {
    RankingFFAPickerSteps[RankingFFAPickerSteps["Round"] = 0] = "Round";
    RankingFFAPickerSteps[RankingFFAPickerSteps["Group"] = 1] = "Group";
})(RankingFFAPickerSteps || (RankingFFAPickerSteps = {}));
class RankingFfaPickerComponent {
    constructor() {
        this.step = RankingFFAPickerSteps.Round;
        this.picked = new EventEmitter();
        this.cancel = new EventEmitter();
    }
    ngOnChanges(changes) {
        if (changes["selectedGroup"]) {
            this.pickedGroup = Object.assign({}, this.selectedGroup);
        }
    }
    pickRound(round) {
        if (round !== this.pickedGroup.round) {
            this.pickedGroup.group = 0;
        }
        this.pickedGroup.round = round;
        this.goFurther();
    }
    pickGroup(group) {
        this.pickedGroup.group = group;
        this.done();
    }
    cancelAction() {
        this.cancel.emit();
    }
    done() {
        this.picked.emit({
            round: this.pickedGroup.round,
            group: this.pickedGroup.group,
        });
    }
    goBack() {
        this.step--;
    }
    goFurther() {
        this.step++;
    }
    setStep(step) {
        this.step = step;
    }
}
RankingFfaPickerComponent.ɵfac = function RankingFfaPickerComponent_Factory(t) { return new (t || RankingFfaPickerComponent)(); };
RankingFfaPickerComponent.ɵcmp = i0.ɵɵdefineComponent({ type: RankingFfaPickerComponent, selectors: [["ranking-ffa-picker"]], inputs: { step: "step", rounds: "rounds", groups: "groups", playerGroups: "playerGroups", currentRound: "currentRound", selectedGroup: "selectedGroup" }, outputs: { picked: "picked", cancel: "cancel" }, features: [i0.ɵɵNgOnChangesFeature], decls: 21, vars: 24, consts: [[1, "c-ranking-ffa-picker", 3, "step", "stepped"], [3, "title"], [3, "rounds", "currentRound", "selectedRound", "picked"], [1, "c-ranking-ffa-picker__buttons"], [1, "c-button--big", "c-button--primary", "c-ranking-ffa-picker__button", 3, "clicked"], [1, "c-button--big", "c-button--empty", "c-ranking-ffa-picker__button", 3, "clicked"], [3, "groups", "selectedGroup", "picked"]], template: function RankingFfaPickerComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "stepper", 0);
        i0.ɵɵlistener("stepped", function RankingFfaPickerComponent_Template_stepper_stepped_0_listener($event) { return ctx.setStep($event); });
        i0.ɵɵelementStart(1, "step", 1);
        i0.ɵɵpipe(2, "translate");
        i0.ɵɵelementStart(3, "ranking-round-picker", 2);
        i0.ɵɵlistener("picked", function RankingFfaPickerComponent_Template_ranking_round_picker_picked_3_listener($event) { return ctx.pickRound($event); });
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(4, "div", 3);
        i0.ɵɵelementStart(5, "play-button", 4);
        i0.ɵɵlistener("clicked", function RankingFfaPickerComponent_Template_play_button_clicked_5_listener() { return ctx.goFurther(); });
        i0.ɵɵtext(6);
        i0.ɵɵpipe(7, "translate");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(8, "play-button", 5);
        i0.ɵɵlistener("clicked", function RankingFfaPickerComponent_Template_play_button_clicked_8_listener() { return ctx.cancelAction(); });
        i0.ɵɵtext(9);
        i0.ɵɵpipe(10, "translate");
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(11, "step", 1);
        i0.ɵɵpipe(12, "translate");
        i0.ɵɵelementStart(13, "ranking-group-picker", 6);
        i0.ɵɵlistener("picked", function RankingFfaPickerComponent_Template_ranking_group_picker_picked_13_listener($event) { return ctx.pickGroup($event); });
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(14, "div", 3);
        i0.ɵɵelementStart(15, "play-button", 4);
        i0.ɵɵlistener("clicked", function RankingFfaPickerComponent_Template_play_button_clicked_15_listener() { return ctx.done(); });
        i0.ɵɵtext(16);
        i0.ɵɵpipe(17, "translate");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(18, "play-button", 5);
        i0.ɵɵlistener("clicked", function RankingFfaPickerComponent_Template_play_button_clicked_18_listener() { return ctx.goBack(); });
        i0.ɵɵtext(19);
        i0.ɵɵpipe(20, "translate");
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("step", ctx.step);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("title", i0.ɵɵpipeBind1(2, 12, "ranking-ffa-picker:select-round"));
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("rounds", ctx.rounds)("currentRound", ctx.currentRound)("selectedRound", ctx.pickedGroup.round);
        i0.ɵɵadvance(3);
        i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(7, 14, "commons:continue"), " ");
        i0.ɵɵadvance(3);
        i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(10, 16, "commons:cancel"), " ");
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("title", i0.ɵɵpipeBind1(12, 18, "ranking-ffa-picker:select-group"));
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("groups", ctx.groups[ctx.pickedGroup.round])("selectedGroup", ctx.pickedGroup.group);
        i0.ɵɵadvance(3);
        i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(17, 20, "commons:confirm"), " ");
        i0.ɵɵadvance(3);
        i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(20, 22, "commons:back"), " ");
    } }, directives: [i1.StepperComponent, i1.StepComponent, i2$2.RankingRoundPickerComponent, i1.ButtonComponent, i2$2.RankingGroupPickerComponent], pipes: [i2.TranslatePipe], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RankingFfaPickerComponent, [{
        type: Component,
        args: [{
                selector: "ranking-ffa-picker",
                templateUrl: "./ranking-ffa-picker.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, { step: [{
            type: Input
        }], rounds: [{
            type: Input
        }], groups: [{
            type: Input
        }], playerGroups: [{
            type: Input
        }], currentRound: [{
            type: Input
        }], selectedGroup: [{
            type: Input
        }], picked: [{
            type: Output
        }], cancel: [{
            type: Output
        }] }); })();

const rankingFFAPickerTexts = {
    "ranking-ffa-picker:select-round": "Select Round",
    "ranking-ffa-picker:select-group": "Select Group",
    "commons:back": "back",
    "commons:continue": "continue",
    "commons:confirm": "confirm",
    "commons:cancel": "cancel",
};

const _c0$2 = function (a0, a1) { return { current: a0, max: a1 }; };
class RankingFFAGroupSwitchComponent {
    constructor() {
        this.rounds = [];
        this.selected = new EventEmitter();
        this.switchRound = new EventEmitter();
        this.switchGroup = new EventEmitter();
    }
    setRound(round) {
        this.selected.emit({
            round: round,
            group: 0,
        });
    }
    setGroup(group) {
        this.selected.emit({
            round: this.selectedGroup.round,
            group: group,
        });
    }
}
RankingFFAGroupSwitchComponent.ɵfac = function RankingFFAGroupSwitchComponent_Factory(t) { return new (t || RankingFFAGroupSwitchComponent)(); };
RankingFFAGroupSwitchComponent.ɵcmp = i0.ɵɵdefineComponent({ type: RankingFFAGroupSwitchComponent, selectors: [["ranking-ffa-group-switch"]], inputs: { rounds: "rounds", groups: "groups", selectedGroup: "selectedGroup" }, outputs: { selected: "selected", switchRound: "switchRound", switchGroup: "switchGroup" }, decls: 7, vars: 18, consts: [[1, "o-grid", "o-grid--inside"], [1, "o-grid__item", "u-1/2", 3, "max", "page", "paginated", "switch"]], template: function RankingFFAGroupSwitchComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelementStart(1, "ranking-switcher", 1);
        i0.ɵɵlistener("paginated", function RankingFFAGroupSwitchComponent_Template_ranking_switcher_paginated_1_listener($event) { return ctx.setRound($event + 1); })("switch", function RankingFFAGroupSwitchComponent_Template_ranking_switcher_switch_1_listener() { return ctx.switchRound.emit(); });
        i0.ɵɵtext(2);
        i0.ɵɵpipe(3, "translate");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(4, "ranking-switcher", 1);
        i0.ɵɵlistener("paginated", function RankingFFAGroupSwitchComponent_Template_ranking_switcher_paginated_4_listener($event) { return ctx.setGroup($event); })("switch", function RankingFFAGroupSwitchComponent_Template_ranking_switcher_switch_4_listener() { return ctx.switchGroup.emit(); });
        i0.ɵɵtext(5);
        i0.ɵɵpipe(6, "translate");
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("max", ctx.rounds.length - 1)("page", ctx.selectedGroup.round - 1);
        i0.ɵɵadvance(1);
        i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(3, 6, "ranking-ffa-group-switch:round-pagination", i0.ɵɵpureFunction2(12, _c0$2, ctx.selectedGroup.round, ctx.rounds.length)), " ");
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("max", ctx.groups[ctx.selectedGroup.round].length - 1)("page", ctx.selectedGroup.group);
        i0.ɵɵadvance(1);
        i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(6, 9, "ranking-ffa-group-switch:group-pagination", i0.ɵɵpureFunction2(15, _c0$2, ctx.selectedGroup.group + 1, ctx.groups[ctx.selectedGroup.round].length)), " ");
    } }, directives: [i2$2.RankingSwitcherComponent], pipes: [i2.TranslatePipe], styles: [""], changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RankingFFAGroupSwitchComponent, [{
        type: Component,
        args: [{
                selector: "ranking-ffa-group-switch",
                templateUrl: "./ranking-ffa-group-switch.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
                styleUrls: ["./ranking-ffa-group-switch.component.scss"],
            }]
    }], null, { rounds: [{
            type: Input
        }], groups: [{
            type: Input
        }], selectedGroup: [{
            type: Input
        }], selected: [{
            type: Output
        }], switchRound: [{
            type: Output
        }], switchGroup: [{
            type: Output
        }] }); })();

const rankingFFAGroupSwitchTexts = {
    "ranking-ffa-group-switch:round-pagination": "Round {current}/{max}",
    "ranking-ffa-group-switch:group-pagination": "Group {current}/{max}",
};

function RankingFFAOverallSwitchComponent_div_1_Template(rf, ctx) { if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 1);
    i0.ɵɵelementStart(1, "div", 2);
    i0.ɵɵelementStart(2, "button", 3);
    i0.ɵɵlistener("click", function RankingFFAOverallSwitchComponent_div_1_Template_button_click_2_listener() { i0.ɵɵrestoreView(_r3); const ctx_r2 = i0.ɵɵnextContext(); return ctx_r2.toggleOverall.emit(); });
    i0.ɵɵelement(3, "span");
    i0.ɵɵelementStart(4, "span");
    i0.ɵɵtext(5);
    i0.ɵɵpipe(6, "translate");
    i0.ɵɵelementEnd();
    i0.ɵɵelement(7, "svg-icon", 4);
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance(5);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(6, 6, "ranking-ffa-picker:go-to-overall-ranking"));
    i0.ɵɵadvance(2);
    i0.ɵɵstyleProp("width", 24, "px")("height", 24, "px");
    i0.ɵɵproperty("icon", "chevron-right");
} }
function RankingFFAOverallSwitchComponent_div_2_Template(rf, ctx) { if (rf & 1) {
    const _r5 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 1);
    i0.ɵɵelementStart(1, "div", 5);
    i0.ɵɵelementStart(2, "button", 3);
    i0.ɵɵlistener("click", function RankingFFAOverallSwitchComponent_div_2_Template_button_click_2_listener() { i0.ɵɵrestoreView(_r5); const ctx_r4 = i0.ɵɵnextContext(); return ctx_r4.toggleOverall.emit(); });
    i0.ɵɵelement(3, "svg-icon", 4);
    i0.ɵɵelementStart(4, "span");
    i0.ɵɵtext(5);
    i0.ɵɵpipe(6, "translate");
    i0.ɵɵelementEnd();
    i0.ɵɵelement(7, "span");
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵelement(8, "div", 6);
    i0.ɵɵelementStart(9, "ranking-switcher", 7);
    i0.ɵɵlistener("paginated", function RankingFFAOverallSwitchComponent_div_2_Template_ranking_switcher_paginated_9_listener($event) { i0.ɵɵrestoreView(_r5); const ctx_r6 = i0.ɵɵnextContext(); return ctx_r6.setPage.emit($event); });
    i0.ɵɵtext(10);
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(3);
    i0.ɵɵstyleProp("width", 24, "px")("height", 24, "px");
    i0.ɵɵproperty("icon", "chevron-left");
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(6, 9, "ranking-ffa-picker:round-rankings"));
    i0.ɵɵadvance(4);
    i0.ɵɵproperty("max", ctx_r1.maxPages)("page", ctx_r1.paginationOptions.page);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", ctx_r1.pagedEntries, " ");
} }
class RankingFFAOverallSwitchComponent {
    constructor() {
        this.toggleOverall = new EventEmitter();
        this.setPage = new EventEmitter();
    }
    ngOnChanges() {
        const { page, perPage } = this.paginationOptions;
        const start = page * perPage;
        const end = start + perPage;
        this.maxPages = Math.ceil(this.entriesSize / this.paginationOptions.perPage) - 1;
        this.pagedEntries = `${start + 1}-${end > this.entriesSize ? this.entriesSize : end}`;
    }
}
RankingFFAOverallSwitchComponent.ɵfac = function RankingFFAOverallSwitchComponent_Factory(t) { return new (t || RankingFFAOverallSwitchComponent)(); };
RankingFFAOverallSwitchComponent.ɵcmp = i0.ɵɵdefineComponent({ type: RankingFFAOverallSwitchComponent, selectors: [["ranking-ffa-overall-switch"]], inputs: { ffaOverallRankingActive: "ffaOverallRankingActive", paginationOptions: "paginationOptions", entriesSize: "entriesSize" }, outputs: { toggleOverall: "toggleOverall", setPage: "setPage" }, features: [i0.ɵɵNgOnChangesFeature], decls: 3, vars: 2, consts: [["class", "o-grid o-grid--inside", 4, "ngIf"], [1, "o-grid", "o-grid--inside"], [1, "o-grid__item"], [1, "c-button", "c-ranking-switcher__control", "c-ranking-type-switch", 3, "click"], [3, "icon"], [1, "o-grid__item", "u-1/4"], [1, "o-grid__item", "u-1/2"], [1, "o-grid__item", "u-1/4", 3, "max", "page", "paginated"]], template: function RankingFFAOverallSwitchComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div");
        i0.ɵɵtemplate(1, RankingFFAOverallSwitchComponent_div_1_Template, 8, 8, "div", 0);
        i0.ɵɵtemplate(2, RankingFFAOverallSwitchComponent_div_2_Template, 11, 11, "div", 0);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", !ctx.ffaOverallRankingActive);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.ffaOverallRankingActive);
    } }, directives: [i7.NgIf, i1.SvgIconComponent, i2$2.RankingSwitcherComponent], pipes: [i2.TranslatePipe], styles: ["[_nghost-%COMP%]   .c-ranking-type-switch[_ngcontent-%COMP%]{background:#fff;margin-bottom:1.5rem;padding:.75rem;width:100%;display:flex;align-items:center;justify-content:space-between;box-shadow:none;border-radius:0}"], changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RankingFFAOverallSwitchComponent, [{
        type: Component,
        args: [{
                selector: "ranking-ffa-overall-switch",
                templateUrl: "./ranking-ffa-overall-switch.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
                styleUrls: ["./ranking-ffa-overall-switch.component.scss"],
            }]
    }], null, { ffaOverallRankingActive: [{
            type: Input
        }], paginationOptions: [{
            type: Input
        }], toggleOverall: [{
            type: Output
        }], setPage: [{
            type: Output
        }], entriesSize: [{
            type: Input
        }] }); })();

class RankingFfaGroupSwitchSkeletonComponent {
}
RankingFfaGroupSwitchSkeletonComponent.ɵfac = function RankingFfaGroupSwitchSkeletonComponent_Factory(t) { return new (t || RankingFfaGroupSwitchSkeletonComponent)(); };
RankingFfaGroupSwitchSkeletonComponent.ɵcmp = i0.ɵɵdefineComponent({ type: RankingFfaGroupSwitchSkeletonComponent, selectors: [["ranking-ffa-group-switch-skeleton"]], decls: 3, vars: 0, consts: [[1, "o-grid", "o-grid--inside"], [1, "o-grid__item", "u-1/2"]], template: function RankingFfaGroupSwitchSkeletonComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelement(1, "ranking-switcher-skeleton", 1);
        i0.ɵɵelement(2, "ranking-switcher-skeleton", 1);
        i0.ɵɵelementEnd();
    } }, directives: [i2$2.RankingSwitcherSkeletonComponent], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RankingFfaGroupSwitchSkeletonComponent, [{
        type: Component,
        args: [{
                selector: "ranking-ffa-group-switch-skeleton",
                templateUrl: "./ranking-ffa-group-switch-skeleton.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, null); })();

function FfaRankingSkeletonComponent_section_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "section", 11);
    i0.ɵɵelement(1, "ranking-ffa-group-switch-skeleton");
    i0.ɵɵelementEnd();
} }
const _c0$1 = function () { return { number: 1 }; };
const _c1 = function () { return { number: 2 }; };
const _c2 = function () { return { number: 3 }; };
class FfaRankingSkeletonComponent {
    constructor() {
        this.withSwitcher = true;
    }
}
FfaRankingSkeletonComponent.ɵfac = function FfaRankingSkeletonComponent_Factory(t) { return new (t || FfaRankingSkeletonComponent)(); };
FfaRankingSkeletonComponent.ɵcmp = i0.ɵɵdefineComponent({ type: FfaRankingSkeletonComponent, selectors: [["ffa-ranking-skeleton"]], inputs: { withSwitcher: "withSwitcher" }, decls: 46, vars: 29, consts: [["class", "u-baseline", 4, "ngIf"], [1, "u-baseline-tiny", "o-grid", "o-grid--inside"], [1, "o-grid__item", "u-1/3", "u-baseline-small"], [1, "u-divided", 3, "expand"], ["expansion-panel-header", "", 1, "o-grid", "o-grid--align-center", "o-grid--inside"], [1, "o-grid__item", "u-1/2"], [1, "o-grid__item", "u-2/4"], [3, "label"], [1, "c-skeleton__text", "c-skeleton__text--tiny", "c-skeleton__text--inline"], ["expansion-panel-panel", ""], [1, "u-divided"], [1, "u-baseline"]], template: function FfaRankingSkeletonComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, FfaRankingSkeletonComponent_section_0_Template, 2, 0, "section", 0);
        i0.ɵɵelementStart(1, "section", 1);
        i0.ɵɵelementStart(2, "ranking-match-skeleton", 2);
        i0.ɵɵtext(3);
        i0.ɵɵpipe(4, "translate");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(5, "ranking-match-skeleton", 2);
        i0.ɵɵtext(6);
        i0.ɵɵpipe(7, "translate");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(8, "ranking-match-skeleton", 2);
        i0.ɵɵtext(9);
        i0.ɵɵpipe(10, "translate");
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(11, "section");
        i0.ɵɵelementStart(12, "expansion-panel", 3);
        i0.ɵɵelementStart(13, "div", 4);
        i0.ɵɵelementStart(14, "div", 5);
        i0.ɵɵelement(15, "ranking-contestant-skeleton");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(16, "div", 6);
        i0.ɵɵelementStart(17, "labeled-value", 7);
        i0.ɵɵpipe(18, "translate");
        i0.ɵɵelement(19, "div", 8);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(20, "div", 9);
        i0.ɵɵelement(21, "ranking-members-skeleton");
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(22, "expansion-panel", 10);
        i0.ɵɵelementStart(23, "div", 4);
        i0.ɵɵelementStart(24, "div", 5);
        i0.ɵɵelement(25, "ranking-contestant-skeleton");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(26, "div", 6);
        i0.ɵɵelementStart(27, "labeled-value", 7);
        i0.ɵɵpipe(28, "translate");
        i0.ɵɵelement(29, "div", 8);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(30, "expansion-panel", 10);
        i0.ɵɵelementStart(31, "div", 4);
        i0.ɵɵelementStart(32, "div", 5);
        i0.ɵɵelement(33, "ranking-contestant-skeleton");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(34, "div", 6);
        i0.ɵɵelementStart(35, "labeled-value", 7);
        i0.ɵɵpipe(36, "translate");
        i0.ɵɵelement(37, "div", 8);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(38, "expansion-panel", 10);
        i0.ɵɵelementStart(39, "div", 4);
        i0.ɵɵelementStart(40, "div", 5);
        i0.ɵɵelement(41, "ranking-contestant-skeleton");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(42, "div", 6);
        i0.ɵɵelementStart(43, "labeled-value", 7);
        i0.ɵɵpipe(44, "translate");
        i0.ɵɵelement(45, "div", 8);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.withSwitcher);
        i0.ɵɵadvance(3);
        i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(4, 9, "ffa-match:match-number", i0.ɵɵpureFunction0(26, _c0$1)), " ");
        i0.ɵɵadvance(3);
        i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(7, 12, "ffa-match:match-number", i0.ɵɵpureFunction0(27, _c1)), " ");
        i0.ɵɵadvance(3);
        i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(10, 15, "ffa-match:match-number", i0.ɵɵpureFunction0(28, _c2)), " ");
        i0.ɵɵadvance(3);
        i0.ɵɵproperty("expand", false);
        i0.ɵɵadvance(5);
        i0.ɵɵproperty("label", i0.ɵɵpipeBind1(18, 18, "ffa-ranking:total-points"));
        i0.ɵɵadvance(10);
        i0.ɵɵproperty("label", i0.ɵɵpipeBind1(28, 20, "ffa-ranking:total-points"));
        i0.ɵɵadvance(8);
        i0.ɵɵproperty("label", i0.ɵɵpipeBind1(36, 22, "ffa-ranking:total-points"));
        i0.ɵɵadvance(8);
        i0.ɵɵproperty("label", i0.ɵɵpipeBind1(44, 24, "ffa-ranking:total-points"));
    } }, directives: [i7.NgIf, i2$2.RankingMatchSkeletonComponent, i1.ExpansionPanelComponent, i2$2.RankingContestantSkeletonComponent, i1.LabeledValueComponent, i2$2.RankingMembersSkeletonComponent, RankingFfaGroupSwitchSkeletonComponent], pipes: [i2.TranslatePipe], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FfaRankingSkeletonComponent, [{
        type: Component,
        args: [{
                selector: "ffa-ranking-skeleton",
                templateUrl: "./ffa-ranking-skeleton.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, { withSwitcher: [{
            type: Input
        }] }); })();

function FfaRankingEntryComponent_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "div", 2);
    i0.ɵɵelementStart(2, "labeled-value", 3);
    i0.ɵɵpipe(3, "translate");
    i0.ɵɵtext(4);
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(5, "div", 2);
    i0.ɵɵelementStart(6, "labeled-value", 3);
    i0.ɵɵpipe(7, "translate");
    i0.ɵɵtext(8);
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("label", i0.ɵɵpipeBind1(3, 4, "ffa-ranking:total-points"));
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" ", ctx_r0.contestant.points, " ");
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("label", i0.ɵɵpipeBind1(7, 6, "ffa-ranking:tiebreaker-points"));
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" ", ctx_r0.contestant.tiebreaker, " ");
} }
function FfaRankingEntryComponent_ng_container_2_div_10_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 2);
    i0.ɵɵelementStart(1, "labeled-value", 4);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵpipe(3, "translate");
    i0.ɵɵtext(4);
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r3 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("tooltip", i0.ɵɵpipeBind1(2, 3, "ffa-ranking:highest-placement"))("label", i0.ɵɵpipeBind1(3, 5, "ffa-ranking:highest-placement"));
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate1(" ", ctx_r3.contestant.tiebreaker[1], " ");
} }
function FfaRankingEntryComponent_ng_container_2_div_11_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 2);
    i0.ɵɵelementStart(1, "labeled-value", 4);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵpipe(3, "translate");
    i0.ɵɵtext(4);
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("tooltip", i0.ɵɵpipeBind1(2, 3, "ffa-ranking:most-kills"))("label", i0.ɵɵpipeBind1(3, 5, "ffa-ranking:most-kills"));
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate1(" ", ctx_r4.contestant.tiebreaker[2], " ");
} }
function FfaRankingEntryComponent_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "div", 2);
    i0.ɵɵelementStart(2, "labeled-value", 3);
    i0.ɵɵpipe(3, "translate");
    i0.ɵɵtext(4);
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(5, "div", 2);
    i0.ɵɵelementStart(6, "labeled-value", 4);
    i0.ɵɵpipe(7, "translate");
    i0.ɵɵpipe(8, "translate");
    i0.ɵɵtext(9);
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(10, FfaRankingEntryComponent_ng_container_2_div_10_Template, 5, 7, "div", 5);
    i0.ɵɵtemplate(11, FfaRankingEntryComponent_ng_container_2_div_11_Template, 5, 7, "div", 5);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("label", i0.ɵɵpipeBind1(3, 7, "ffa-ranking:total-points"));
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" ", ctx_r1.contestant.points, " ");
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("tooltip", i0.ɵɵpipeBind1(7, 9, "ffa-ranking:most-points"))("label", i0.ɵɵpipeBind1(8, 11, "ffa-ranking:most-points"));
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate1(" ", ctx_r1.contestant.tiebreaker[0], " ");
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.hasHighestplacement());
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.hasMostKills());
} }
function FfaRankingEntryComponent_ng_container_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "div", 6);
    i0.ɵɵelementStart(2, "labeled-value", 3);
    i0.ɵɵpipe(3, "translate");
    i0.ɵɵtext(4);
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("label", i0.ɵɵpipeBind1(3, 2, "ffa-ranking:total-points"));
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" ", ctx_r2.contestant.points, " ");
} }
class FfaRankingEntryComponent {
    constructor() {
        this.expansionChanged = new EventEmitter();
        this.expandable = true;
    }
    hasHighestplacement() {
        return Array.isArray(this.contestant.tiebreaker) && this.contestant.tiebreaker.length > 1;
    }
    hasMostKills() {
        return Array.isArray(this.contestant.tiebreaker) && this.contestant.tiebreaker.length > 2;
    }
}
FfaRankingEntryComponent.ɵfac = function FfaRankingEntryComponent_Factory(t) { return new (t || FfaRankingEntryComponent)(); };
FfaRankingEntryComponent.ɵcmp = i0.ɵɵdefineComponent({ type: FfaRankingEntryComponent, selectors: [["ffa-ranking-entry"]], inputs: { isExpanded: "isExpanded", members: "members", contestant: "contestant", integration: "integration", expandable: "expandable" }, outputs: { expansionChanged: "expansionChanged" }, decls: 4, vars: 7, consts: [[3, "isExpanded", "contestant", "members", "expandable", "expansionChanged"], [4, "ngIf"], [1, "o-grid__item", "u-1/4"], [3, "label"], [3, "tooltip", "label"], ["class", "o-grid__item u-1/4", 4, "ngIf"], [1, "o-grid__item", "u-2/4"]], template: function FfaRankingEntryComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "ranking-entry", 0);
        i0.ɵɵlistener("expansionChanged", function FfaRankingEntryComponent_Template_ranking_entry_expansionChanged_0_listener($event) { return ctx.expansionChanged.emit($event); });
        i0.ɵɵtemplate(1, FfaRankingEntryComponent_ng_container_1_Template, 9, 8, "ng-container", 1);
        i0.ɵɵtemplate(2, FfaRankingEntryComponent_ng_container_2_Template, 12, 13, "ng-container", 1);
        i0.ɵɵtemplate(3, FfaRankingEntryComponent_ng_container_3_Template, 5, 4, "ng-container", 1);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("isExpanded", ctx.isExpanded)("contestant", ctx.contestant)("members", ctx.members)("expandable", ctx.expandable);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.integration && !ctx.integration.includes("gameintegrationapexlegends"));
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.integration && ctx.integration.includes("gameintegrationapexlegends"));
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", !ctx.integration);
    } }, directives: [i2$2.RankingEntryComponent, i7.NgIf, i1.LabeledValueComponent, i1.TooltipDirective], pipes: [i2.TranslatePipe], styles: ["[_nghost-%COMP%] {\n                display: block;\n            }"], changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FfaRankingEntryComponent, [{
        type: Component,
        args: [{
                selector: "ffa-ranking-entry",
                templateUrl: "./ffa-ranking-entry.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
                styles: [
                    `
            :host {
                display: block;
            }
        `,
                ],
            }]
    }], null, { isExpanded: [{
            type: Input
        }], members: [{
            type: Input
        }], contestant: [{
            type: Input
        }], integration: [{
            type: Input
        }], expansionChanged: [{
            type: Output
        }], expandable: [{
            type: Input
        }] }); })();

function FfaRankingComponent_section_0_ranking_ffa_overall_switch_1_Template(rf, ctx) { if (rf & 1) {
    const _r9 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "ranking-ffa-overall-switch", 7);
    i0.ɵɵlistener("setPage", function FfaRankingComponent_section_0_ranking_ffa_overall_switch_1_Template_ranking_ffa_overall_switch_setPage_0_listener($event) { i0.ɵɵrestoreView(_r9); const ctx_r8 = i0.ɵɵnextContext(2); return ctx_r8.setPage($event); })("toggleOverall", function FfaRankingComponent_section_0_ranking_ffa_overall_switch_1_Template_ranking_ffa_overall_switch_toggleOverall_0_listener() { i0.ɵɵrestoreView(_r9); const ctx_r10 = i0.ɵɵnextContext(2); return ctx_r10.toggleOverall(); });
    i0.ɵɵpipe(1, "async");
    i0.ɵɵpipe(2, "async");
    i0.ɵɵpipe(3, "async");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r6 = i0.ɵɵnextContext(2);
    let tmp_2_0 = null;
    i0.ɵɵproperty("ffaOverallRankingActive", i0.ɵɵpipeBind1(1, 3, ctx_r6.ffaOverallRankingActive$))("paginationOptions", i0.ɵɵpipeBind1(2, 5, ctx_r6.paginationOptions$))("entriesSize", (tmp_2_0 = i0.ɵɵpipeBind1(3, 7, ctx_r6.ranking$)) == null ? null : tmp_2_0.ranking.length);
} }
function FfaRankingComponent_section_0_ranking_ffa_group_switch_3_Template(rf, ctx) { if (rf & 1) {
    const _r12 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "ranking-ffa-group-switch", 8);
    i0.ɵɵlistener("switchRound", function FfaRankingComponent_section_0_ranking_ffa_group_switch_3_Template_ranking_ffa_group_switch_switchRound_0_listener() { i0.ɵɵrestoreView(_r12); const ctx_r11 = i0.ɵɵnextContext(2); return ctx_r11.openPickerDialog(0); })("switchGroup", function FfaRankingComponent_section_0_ranking_ffa_group_switch_3_Template_ranking_ffa_group_switch_switchGroup_0_listener() { i0.ɵɵrestoreView(_r12); const ctx_r13 = i0.ɵɵnextContext(2); return ctx_r13.openPickerDialog(1); })("selected", function FfaRankingComponent_section_0_ranking_ffa_group_switch_3_Template_ranking_ffa_group_switch_selected_0_listener($event) { i0.ɵɵrestoreView(_r12); const ctx_r14 = i0.ɵɵnextContext(2); return ctx_r14.selectGroup($event); });
    i0.ɵɵpipe(1, "async");
    i0.ɵɵpipe(2, "async");
    i0.ɵɵpipe(3, "async");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r7 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("rounds", i0.ɵɵpipeBind1(1, 3, ctx_r7.rounds$))("groups", i0.ɵɵpipeBind1(2, 5, ctx_r7.groups$))("selectedGroup", i0.ɵɵpipeBind1(3, 7, ctx_r7.selectedGroup$));
} }
function FfaRankingComponent_section_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "section", 4);
    i0.ɵɵtemplate(1, FfaRankingComponent_section_0_ranking_ffa_overall_switch_1_Template, 4, 9, "ranking-ffa-overall-switch", 5);
    i0.ɵɵpipe(2, "async");
    i0.ɵɵtemplate(3, FfaRankingComponent_section_0_ranking_ffa_group_switch_3_Template, 4, 9, "ranking-ffa-group-switch", 6);
    i0.ɵɵpipe(4, "async");
    i0.ɵɵpipe(5, "async");
    i0.ɵɵpipe(6, "async");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    let tmp_1_0 = null;
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(2, 2, ctx_r0.ffaOverallRanking$));
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", ((tmp_1_0 = i0.ɵɵpipeBind1(4, 4, ctx_r0.rounds$)) == null ? null : tmp_1_0.length) && (!i0.ɵɵpipeBind1(5, 6, ctx_r0.ffaOverallRanking$) || !i0.ɵɵpipeBind1(6, 8, ctx_r0.ffaOverallRankingActive$)));
} }
function FfaRankingComponent_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0);
} }
function FfaRankingComponent_ng_template_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "ffa-ranking-skeleton", 9);
    i0.ɵɵpipe(1, "async");
} if (rf & 2) {
    const ctx_r3 = i0.ɵɵnextContext();
    i0.ɵɵproperty("withSwitcher", !i0.ɵɵpipeBind1(1, 1, ctx_r3.results$).entry);
} }
const _c0 = function (a0) { return { number: a0 }; };
function FfaRankingComponent_ng_template_6_article_0_section_1_ranking_match_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "ranking-match", 14);
    i0.ɵɵpipe(1, "async");
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵtext(3);
    i0.ɵɵpipe(4, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const entry_r20 = ctx.$implicit;
    const i_r21 = ctx.index;
    i0.ɵɵproperty("link", i0.ɵɵpipeBind1(1, 4, entry_r20.link$))("state", i0.ɵɵpipeBind1(2, 6, "commons:match-state-" + entry_r20.match.state))("startTime", entry_r20.startTime);
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(4, 8, "ffa-match:match-number", i0.ɵɵpureFunction1(11, _c0, i_r21 + 1)), " ");
} }
function FfaRankingComponent_ng_template_6_article_0_section_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "section", 12);
    i0.ɵɵtemplate(1, FfaRankingComponent_ng_template_6_article_0_section_1_ranking_match_1_Template, 5, 13, "ranking-match", 13);
    i0.ɵɵpipe(2, "async");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r17 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", i0.ɵɵpipeBind1(2, 2, ctx_r17.matchEntries$))("ngForTrackBy", ctx_r17.trackMatchEntries);
} }
function FfaRankingComponent_ng_template_6_article_0_section_3_ffa_ranking_entry_1_Template(rf, ctx) { if (rf & 1) {
    const _r26 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "ffa-ranking-entry", 16);
    i0.ɵɵlistener("expansionChanged", function FfaRankingComponent_ng_template_6_article_0_section_3_ffa_ranking_entry_1_Template_ffa_ranking_entry_expansionChanged_0_listener($event) { i0.ɵɵrestoreView(_r26); const entry_r24 = ctx.$implicit; const ctx_r25 = i0.ɵɵnextContext(4); return ctx_r25.expansionChanged($event, entry_r24.contestant.id); });
    i0.ɵɵpipe(1, "async");
    i0.ɵɵpipe(2, "async");
    i0.ɵɵpipe(3, "async");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const entry_r24 = ctx.$implicit;
    const ctx_r23 = i0.ɵɵnextContext(4);
    i0.ɵɵproperty("expandable", i0.ɵɵpipeBind1(1, 5, ctx_r23.leagueMode$) === 0)("isExpanded", false)("contestant", entry_r24.contestant)("members", i0.ɵɵpipeBind1(2, 7, entry_r24.members$))("integration", i0.ɵɵpipeBind1(3, 9, ctx_r23.league$).entry.gameIntegration);
} }
function FfaRankingComponent_ng_template_6_article_0_section_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "section");
    i0.ɵɵtemplate(1, FfaRankingComponent_ng_template_6_article_0_section_3_ffa_ranking_entry_1_Template, 4, 11, "ffa-ranking-entry", 15);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const entries_r22 = ctx.ngIf;
    const ctx_r18 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", entries_r22)("ngForTrackBy", ctx_r18.trackRankingEntries);
} }
function FfaRankingComponent_ng_template_6_article_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "article");
    i0.ɵɵtemplate(1, FfaRankingComponent_ng_template_6_article_0_section_1_Template, 3, 4, "section", 11);
    i0.ɵɵpipe(2, "async");
    i0.ɵɵtemplate(3, FfaRankingComponent_ng_template_6_article_0_section_3_Template, 2, 2, "section", 10);
    i0.ɵɵpipe(4, "async");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r15 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", !i0.ɵɵpipeBind1(2, 2, ctx_r15.ffaOverallRankingActive$));
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(4, 4, ctx_r15.rankingEntries$));
} }
function FfaRankingComponent_ng_template_6_ranking_not_started_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "ranking-not-started");
} }
function FfaRankingComponent_ng_template_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵtemplate(0, FfaRankingComponent_ng_template_6_article_0_Template, 5, 6, "article", 10);
    i0.ɵɵpipe(1, "async");
    i0.ɵɵtemplate(2, FfaRankingComponent_ng_template_6_ranking_not_started_2_Template, 1, 0, "ranking-not-started", 10);
    i0.ɵɵpipe(3, "async");
} if (rf & 2) {
    const ctx_r5 = i0.ɵɵnextContext();
    let tmp_0_0 = null;
    let tmp_1_0 = null;
    i0.ɵɵproperty("ngIf", (tmp_0_0 = i0.ɵɵpipeBind1(1, 2, ctx_r5.rounds$)) == null ? null : tmp_0_0.length);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", !((tmp_1_0 = i0.ɵɵpipeBind1(3, 4, ctx_r5.rounds$)) == null ? null : tmp_1_0.length));
} }
var LeagueMode;
(function (LeagueMode) {
    LeagueMode[LeagueMode["Team"] = 0] = "Team";
    LeagueMode[LeagueMode["Solo"] = 1] = "Solo";
})(LeagueMode || (LeagueMode = {}));
class FfaRankingComponent {
    constructor(store, roundService, groupService, rankingContestantService, linkService, rankingResource, metadataServices, rankingMemberTexts, ds) {
        this.store = store;
        this.roundService = roundService;
        this.groupService = groupService;
        this.rankingContestantService = rankingContestantService;
        this.linkService = linkService;
        this.rankingResource = rankingResource;
        this.metadataServices = metadataServices;
        this.rankingMemberTexts = rankingMemberTexts;
        this.ds = ds;
        this.ranking$ = new BehaviorSubject(null);
        this.ngUnsubscribe = new Subject();
        this.currentPage$ = new BehaviorSubject(0);
        this.entriesPerPage = 100;
        this.paginationActive = false;
        this.expandedTeams$ = new BehaviorSubject([]);
        this.expanded = false;
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    /**
     * Initialize observables
     */
    ngOnInit() {
        this.user$ = this.store.select(getUser);
        this.selectedGroup$ = new BehaviorSubject({ round: 1, group: 0 });
        this.ffaOverallRankingActive$ = new BehaviorSubject(false);
        this.store.dispatch(new LoadLeagueOnceAction(this.leagueId));
        this.league$ = this.store.select(getLeague(this.leagueId)).pipe(filter(entryAvailable));
        this.leagueMode$ = this.league$.pipe(distinct(league => (isTeam(league.entry) ? LeagueMode.Team : LeagueMode.Solo)));
        this.ffaOverallRanking$ = this.league$.pipe(distinct(league => league.entry.enabledFeatures.indexOf("ffa_overall_ranking") !== -1));
        this.contestants$ = this.user$.pipe(filter(entryAvailable), tap(user => this.store.dispatch(new LoadContestantsOnceAction(this.leagueId, +user.entry.id))), switchMap(user => this.store.select(getContestants(this.leagueId, +user.entry.id))), startWith(null));
        this.store.dispatch(new LoadLeagueResultsAction(this.leagueId));
        this.results$ = this.store.select(getLeagueResults(this.leagueId)).pipe(filter(exists));
        this.rounds$ = this.results$.pipe(filter(entryAvailable), map$1(results => {
            return this.roundService.getRoundsFromResults(results.entry);
        }));
        this.groups$ = this.results$.pipe(filter(entryAvailable), map$1(results => {
            return this.groupService.getGroupsFromResults(results.entry);
        }));
        this.playerGroups$ = combineLatest(this.results$, this.contestants$).pipe(map$1(([results, contestants]) => {
            if (!results.entry || !contestants || !contestants.entry) {
                return [];
            }
            const groups = [];
            contestants.entry.forEach(contestant => {
                this.groupService.getContestantGroupsFromResults(results.entry, +contestant.id).forEach(contestantGroup => {
                    groups.push(contestantGroup);
                });
            });
            return groups;
        }));
        this.selectedGroupMatches$ = this.getGroupMatches();
        this.matchEntries$ = this.selectedGroupMatches$.pipe(map$1(matches => matches.map(match => ({
            link$: this.getMatchLink(match.id),
            startTime: match.beginAt,
            match: match,
        }))));
        this.expandedMatches$ = this.store
            .select(getTeamMembersMatches())
            .pipe(map$1(members => uniq(flatten(members.toArray().map(member => member.entry)))));
        this.matchMetaData$ = combineLatest([
            this.selectedGroupMatches$,
            this.league$.pipe(filter(entryAvailable)),
            this.expandedMatches$,
        ]).pipe(switchMap(([matches, league, expandedMatches]) => {
            const { metadata } = this.getGIServices(league.entry);
            if (!metadata) {
                return null;
            }
            return metadata.getMetaData(matches.filter(({ id }) => expandedMatches.indexOf(id) > -1));
        }));
        this.gameaccounts$ = this.selectedGroupMatches$.pipe(filter(matches => Boolean(matches.length)), switchMap(matches => {
            const matchId = matches[0].id;
            this.store.dispatch(new LoadMatchOnceAction(matchId));
            return this.store.select(getMatch(matchId));
        }), filter(entryAvailable), map$1(match => match.entry.gameaccounts), startWith({}));
        this.league$
            .pipe(filter(entryAvailable), first(), tap(league => {
            const round = league.entry.currentRound;
            // Set round to current round if set on the league
            if (round) {
                this.selectGroup({
                    round: round,
                    group: 0,
                });
            }
        }), 
        // Get FFA ranking for ranking calculation
        // Merge map will throw away the league observable on top, this is intended
        // because we want to wait until the first round has been set.
        mergeMap(x => combineLatest(this.selectedGroup$, this.ffaOverallRankingActive$)), shareReplay(1), switchMap(([group, ffaOverallRankingActive]) => {
            const getRanking = ffaOverallRankingActive
                ? this.rankingResource.getRankingOfFFAGroupOfLeague(this.leagueId)
                : this.rankingResource.getRankingOfFFAGroupOfLeague(this.leagueId, group.round, group.group);
            return getRanking.pipe(first(), startWith(null));
        }))
            .subscribe(ranking => this.ranking$.next(ranking));
        this.rankingEntries$ = combineLatest([this.ffaOverallRankingActive$, this.currentPage$]).pipe(switchMap(activeOverall => (activeOverall ? this.getOverallPageContestants() : this.getGroupContestants())), map$1(contestants => contestants.map(contestant => ({
            contestant: contestant,
            members$: this.getMembers(contestant.id),
            isExpanded: this.isExpanded(contestant),
        }))));
        this.paginationOptions$ = this.currentPage$.pipe(map$1(page => {
            return { active: this.paginationActive, page, perPage: this.entriesPerPage };
        }));
        this.hasError$ = this.getHasError();
        this.isReady$ = this.getIsReady();
    }
    getMatchLink(matchId) {
        return this.league$.pipe(filter(entryAvailable), map$1(league => {
            return this.linkService.match(league.entry.uri, matchId);
        }));
    }
    /**
     * @param playerIds
     */
    getPlayerContestants(playerIds, gameaccounts) {
        if (playerIds.length) {
            this.store.dispatch(new LoadPlayersOnceAction(playerIds));
        }
        const participants$ = playerIds.map(playerId => {
            return this.store.select(getPlayer(playerId)).pipe(filter(exists), map$1(player => ({ id: playerId, data: player })), map$1(player => this.rankingContestantService.getRankingContestantFromPlayer(player.id, player.data.entry, gameaccounts[player.id])));
        });
        return combineLatest(participants$);
    }
    /**
     * @param teamIds
     */
    getTeamContestants(teamIds, league) {
        if (teamIds.length) {
            this.store.dispatch(new LoadTeamsOnceAction(teamIds));
        }
        const participants$ = teamIds.map(teamId => {
            return this.store.select(getTeam(teamId)).pipe(filter(exists), filter(team => !!team.entry), take(1), map$1(team => ({ id: teamId, data: team })), map$1(team => this.rankingContestantService.getRankingContestantFromTeam(league.enabledFeatures.indexOf("roster") !== -1, league.uri, team.id, team.data.entry)));
        });
        return forkJoin(participants$);
    }
    /**
     * Handler for lazy loading the expansion panel data when it opens/closes
     *
     * @param state
     * @param contestantId
     */
    expansionChanged(state, contestantId) {
        let expands;
        if (state === ExpansionState.OPEN) {
            this.expandedTeams$
                .pipe(first(), filter(expansions => {
                expands = expansions;
                return expansions.indexOf(contestantId) === -1;
            }), switchMap(() => {
                return combineLatest([this.leagueMode$, this.league$]);
            }), switchMap(([leagueMode, league]) => {
                if (leagueMode === LeagueMode.Team) {
                    this.store.dispatch(new LoadRosterOnceAction(league.entry.id, contestantId));
                    this.store.dispatch(new LoadMembersOnceAction(contestantId));
                    return this.store.select(getTeamMembers(contestantId));
                }
                return of(null);
            }))
                .subscribe(members => {
                this.expandedTeams$.next([...expands, contestantId]);
                if (members && !members.loading) {
                    members.entry.forEach(({ user: { id } }) => {
                        this.store.dispatch(new LoadMembersMatchesOnceAction(id, {
                            leagueIds: [this.leagueId],
                            fields: ["id"],
                        }));
                    });
                }
            });
        }
    }
    /**
     * @param contestant
     */
    isExpanded(contestant) {
        if (this.expanded) {
            return;
        }
        if (contestant.rank === 1) {
            this.expansionChanged(ExpansionState.OPEN, contestant.id);
            this.expanded = true;
        }
        return contestant.rank === 1;
    }
    /**
     * Builds the teammates to be ready for displaying
     *
     * @param metaData
     * @param roster
     * @param members
     */
    buildTeamMembers(metaData, roster, members, league, gameaccount) {
        const { text } = this.getGIServices(league);
        return members.reduce((entryArray, teamMember) => {
            if (roster.members.indexOf(+teamMember.user.id) === -1) {
                return entryArray;
            }
            const rankingContestant = this.rankingContestantService.getRankingContestantFromPlayer(+teamMember.user.id, teamMember.user, gameaccount[teamMember.user.id]);
            const rankingMember = text.getRankingMember(rankingContestant, metaData);
            return [...entryArray, rankingMember];
        }, []);
    }
    /**
     * Get the team members for the expanded expansion panel
     *
     * @param teamId
     */
    getMembers(teamId) {
        return combineLatest([this.expandedTeams$, this.leagueMode$]).pipe(filter(([expandedTeams, leagueMode]) => expandedTeams.indexOf(teamId) !== -1 && leagueMode !== LeagueMode.Solo), first(), switchMap(() => {
            const members$ = this.store.select(getTeamMembers(teamId)).pipe(filter(exists));
            const roster$ = this.store.select(getRoster(this.leagueId, teamId)).pipe(filter(exists));
            return combineLatest([members$, roster$, this.league$, this.matchMetaData$, this.gameaccounts$]);
        }), map$1(([members, roster, league, metaData, gameaccounts]) => {
            const rosterCount = roster.entry ? roster.entry.members.length : league.entry.teamSize;
            if (!members.entry || !roster.entry) {
                return Object.assign(Object.assign({}, members), { entry: {
                        rosterCount,
                        members: [],
                    } });
            }
            const entries = this.buildTeamMembers(metaData, roster.entry, members.entry, league.entry, gameaccounts);
            return Object.assign(Object.assign({}, members), { entry: {
                    rosterCount,
                    members: entries,
                } });
        }));
    }
    /**
     * @param pickStep
     */
    openPickerDialog(pickStep) {
        combineLatest(this.selectedGroup$, this.playerGroups$, this.rounds$, this.groups$)
            .pipe(first())
            .subscribe(([currentGroup, contestantGroups, rounds, groups]) => {
            this.ds
                .openFFAGroupPicker({
                step: pickStep,
                currentGroup: currentGroup,
                groups: groups,
                rounds: rounds,
                contestantGroups: contestantGroups,
                currentRound: rounds.length,
            })
                .then(this.selectGroup.bind(this));
        });
    }
    /**
     * Handler for when a group has been selected
     *
     * @param group
     */
    selectGroup(group) {
        this.selectedGroup$.pipe(first()).subscribe(currentGroup => {
            // Only select group if not the same one
            if (group.group === currentGroup.group && group.round === currentGroup.round) {
                return;
            }
            this.selectedGroup$.next(group);
        });
    }
    toggleOverall() {
        this.currentPage$.next(0);
        this.paginationActive = !this.paginationActive;
        this.ffaOverallRankingActive$.pipe(first()).subscribe(() => this.ffaOverallRankingActive$.next(this.paginationActive));
    }
    getIsReady() {
        return combineLatest(this.ranking$, this.league$, this.results$).pipe(map$1(([ranking, league, results]) => {
            return ranking && [league, results].filter(entry => !entry || (entry.loading && !entry.entry)).length === 0;
        }), distinctUntilChanged());
    }
    getHasError() {
        return combineLatest(this.league$, this.results$).pipe(map$1(data => data.filter(entry => entry.error).length > 0), distinctUntilChanged());
    }
    getGIServices(league) {
        const byGI = service => {
            if (!league.gameIntegration) {
                return service.integration === null;
            }
            return hasIntegration(league, `gameintegration${service.integration}`);
        };
        const metadata = this.metadataServices.find(byGI);
        const text = this.rankingMemberTexts.find(byGI);
        return { text, metadata };
    }
    getGroupMatches() {
        return combineLatest(this.results$, this.selectedGroup$, this.ffaOverallRankingActive$).pipe(map$1(([results, group, ffaOverallRankingActive]) => {
            if (!results.entry) {
                return [];
            }
            return ffaOverallRankingActive ? results.entry : this.groupService.getGroupMatchesFromResults(results.entry, group);
        }));
    }
    getGroupContestants() {
        const contestants$ = combineLatest(this.selectedGroupMatches$, this.league$, this.gameaccounts$).pipe(filter(([matches]) => Boolean(matches.length)), switchMap(([matches, league, gameaccounts]) => {
            const contestantIds = uniq(flatten(matches.map(({ participants }) => participants)).map(({ id }) => id));
            return isPlayer(league.entry)
                ? this.getPlayerContestants(contestantIds, gameaccounts)
                : this.getTeamContestants(contestantIds, league.entry);
        }));
        return combineLatest([this.ranking$, contestants$, this.league$]).pipe(map$1(([ranking, contestants, league]) => {
            const rankingContestants = contestants.map(contestant => this.rankingContestantService.getRankedContestant(contestant, ranking, league.entry.gameIntegration));
            return sortBy(rankingContestants, contestant => {
                if (contestant.rank === 0) {
                    return rankingContestants.length; // just so that we group them at the bottom
                }
                return contestant.rank;
            });
        }));
    }
    getOverallPageContestants() {
        const contestants$ = combineLatest(this.ranking$, this.league$, this.gameaccounts$, this.currentPage$).pipe(filter(([ranking]) => Boolean(ranking)), switchMap(([ranking, league, gameaccounts, currentPage]) => {
            const start = currentPage * this.entriesPerPage;
            const end = start + this.entriesPerPage - 1;
            const contestantIds = ranking.ranking.map(({ id }) => id).slice(start, end);
            return isPlayer(league.entry)
                ? this.getPlayerContestants(contestantIds, gameaccounts)
                : this.getTeamContestants(contestantIds, league.entry);
        }));
        return combineLatest([this.ranking$, contestants$, this.league$]).pipe(map$1(([ranking, contestants, league]) => {
            const rankingContestants = contestants.map(contestant => this.rankingContestantService.getRankedContestant(contestant, ranking, league.entry.gameIntegration));
            return sortBy(rankingContestants, contestant => {
                if (contestant.rank === 0) {
                    return rankingContestants.length; // just so that we group them at the bottom
                }
                return contestant.rank;
            });
        }));
    }
    trackRankingEntries(index, item) {
        return index;
    }
    trackMatchEntries(index, item) {
        return index;
    }
    setPage(page) {
        this.currentPage$.next(page);
    }
}
FfaRankingComponent.ɵfac = function FfaRankingComponent_Factory(t) { return new (t || FfaRankingComponent)(i0.ɵɵdirectiveInject(i2$1.Store), i0.ɵɵdirectiveInject(RankingRoundService), i0.ɵɵdirectiveInject(RankingGroupService), i0.ɵɵdirectiveInject(RankingContestantService), i0.ɵɵdirectiveInject(i1.LinkService), i0.ɵɵdirectiveInject(i6.RankingResource), i0.ɵɵdirectiveInject(MATCH_METADATA), i0.ɵɵdirectiveInject(RANKING_MEMBER_TEXTS), i0.ɵɵdirectiveInject("DialogService")); };
FfaRankingComponent.ɵcmp = i0.ɵɵdefineComponent({ type: FfaRankingComponent, selectors: [["ffa-ranking"]], inputs: { leagueId: "leagueId" }, decls: 8, vars: 8, consts: [["class", "u-baseline", 4, "ngIf"], [4, "ngIf", "ngIfThen", "ngIfElse"], ["loading", ""], ["notLoading", ""], [1, "u-baseline"], [3, "ffaOverallRankingActive", "paginationOptions", "entriesSize", "setPage", "toggleOverall", 4, "ngIf"], [3, "rounds", "groups", "selectedGroup", "switchRound", "switchGroup", "selected", 4, "ngIf"], [3, "ffaOverallRankingActive", "paginationOptions", "entriesSize", "setPage", "toggleOverall"], [3, "rounds", "groups", "selectedGroup", "switchRound", "switchGroup", "selected"], [3, "withSwitcher"], [4, "ngIf"], ["class", "u-baseline-tiny o-grid o-grid--inside", 4, "ngIf"], [1, "u-baseline-tiny", "o-grid", "o-grid--inside"], ["class", "o-grid__item u-1/3 u-baseline-small", 3, "link", "state", "startTime", 4, "ngFor", "ngForOf", "ngForTrackBy"], [1, "o-grid__item", "u-1/3", "u-baseline-small", 3, "link", "state", "startTime"], ["class", "u-divided", 3, "expandable", "isExpanded", "contestant", "members", "integration", "expansionChanged", 4, "ngFor", "ngForOf", "ngForTrackBy"], [1, "u-divided", 3, "expandable", "isExpanded", "contestant", "members", "integration", "expansionChanged"]], template: function FfaRankingComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, FfaRankingComponent_section_0_Template, 7, 10, "section", 0);
        i0.ɵɵpipe(1, "async");
        i0.ɵɵtemplate(2, FfaRankingComponent_ng_container_2_Template, 1, 0, "ng-container", 1);
        i0.ɵɵpipe(3, "async");
        i0.ɵɵtemplate(4, FfaRankingComponent_ng_template_4_Template, 2, 3, "ng-template", null, 2, i0.ɵɵtemplateRefExtractor);
        i0.ɵɵtemplate(6, FfaRankingComponent_ng_template_6_Template, 4, 6, "ng-template", null, 3, i0.ɵɵtemplateRefExtractor);
    } if (rf & 2) {
        const _r2 = i0.ɵɵreference(5);
        const _r4 = i0.ɵɵreference(7);
        i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(1, 4, ctx.results$).entry);
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(3, 6, ctx.isReady$))("ngIfThen", _r4)("ngIfElse", _r2);
    } }, directives: [i7.NgIf, RankingFFAOverallSwitchComponent, RankingFFAGroupSwitchComponent, FfaRankingSkeletonComponent, i7.NgForOf, i2$2.RankingMatchComponent, FfaRankingEntryComponent, i2$2.RankingNotStartedComponent], pipes: [i7.AsyncPipe, i2.TranslatePipe], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FfaRankingComponent, [{
        type: Component,
        args: [{
                selector: "ffa-ranking",
                templateUrl: "./ffa-ranking.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return [{ type: i2$1.Store }, { type: RankingRoundService }, { type: RankingGroupService }, { type: RankingContestantService }, { type: i1.LinkService }, { type: i6.RankingResource }, { type: undefined, decorators: [{
                type: Inject,
                args: [MATCH_METADATA]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: [RANKING_MEMBER_TEXTS]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: ["DialogService"]
            }] }]; }, { leagueId: [{
            type: Input
        }] }); })();

const ffaRankingTexts = {
    "ffa-match:match-number": "Match #{number}",
    "ffa-ranking:total-points": "Total Points",
    "ffa-ranking:tiebreaker-points": "Tiebreaker Points",
    "ffa-ranking:pubg-total-points": "Total Points",
    "ffa-ranking:pubg-tiebreaker-points": "Kill Points",
    "ffa-ranking:member-kills-na": "N/A Kills",
    "ffa-ranking:member-kills": "{kills} Kills",
    "commons:match-state-open": "open",
    "commons:match-state-exception": "exception",
    "commons:match-state-deleted": "deleted",
    "commons:match-state-closed": "closed",
};

class RankingFfaOverallSwitchSkeletonComponent {
}
RankingFfaOverallSwitchSkeletonComponent.ɵfac = function RankingFfaOverallSwitchSkeletonComponent_Factory(t) { return new (t || RankingFfaOverallSwitchSkeletonComponent)(); };
RankingFfaOverallSwitchSkeletonComponent.ɵcmp = i0.ɵɵdefineComponent({ type: RankingFfaOverallSwitchSkeletonComponent, selectors: [["ranking-ffa-overall-switch-skeleton"]], decls: 2, vars: 0, consts: [[1, "o-grid", "o-grid--inside"], [1, "o-grid__item"]], template: function RankingFfaOverallSwitchSkeletonComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelement(1, "ranking-switcher-skeleton", 1);
        i0.ɵɵelementEnd();
    } }, directives: [i2$2.RankingSwitcherSkeletonComponent], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RankingFfaOverallSwitchSkeletonComponent, [{
        type: Component,
        args: [{
                selector: "ranking-ffa-overall-switch-skeleton",
                templateUrl: "./ranking-ffa-overall-switch-skeleton.component.html",
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, null); })();

class FortniteGroupService {
    /**
     * Combines the meta data of multiple matches
     * Resulting meta data should not be used to display the rank since this will be incorrect
     *
     * @param matchMetaData
     */
    combineGroupMetaData(matchMetaData) {
        const contestantGroups = matchMetaData.reduce((data, meta) => {
            meta.contestants.forEach(contestant => {
                if (!data[contestant.id]) {
                    data[contestant.id] = Object.assign(Object.assign({}, contestant), { players: [] });
                }
                data[contestant.id].players = [...data[contestant.id].players, ...contestant.players];
            });
            return data;
        }, {});
        const contestantArray = map(contestantGroups, group => group);
        return contestantArray.reduce((result, contestant) => {
            result.contestants.push(this.combineFortnitePlayers(contestant));
            return result;
        }, { contestants: [] });
    }
    /**
     * Calculates duplicate players in to one object
     *
     * @param contestant
     */
    combineFortnitePlayers(contestant) {
        return contestant.players.reduce((data, player) => {
            const index = findIndex(data.players, p => p.id === player.id);
            if (index === -1) {
                return Object.assign(Object.assign({}, data), { players: [...data.players, player] });
            }
            data.players[index] = Object.assign(Object.assign({}, data.players[index]), { kills: data.players[index].kills + player.kills });
            return data;
        }, Object.assign(Object.assign({}, contestant), { players: [] }));
    }
}
FortniteGroupService.ɵfac = function FortniteGroupService_Factory(t) { return new (t || FortniteGroupService)(); };
FortniteGroupService.ɵprov = i0.ɵɵdefineInjectable({ token: FortniteGroupService, factory: FortniteGroupService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FortniteGroupService, [{
        type: Injectable
    }], null, null); })();

class FortniteMatchMetadataService extends MatchMetadataService {
    constructor(groupService, store) {
        super();
        this.groupService = groupService;
        this.store = store;
        this.integration = "fortnite";
    }
    /**
     * Fetch the metadata of all the matches in a group and combine them as 1 so we can use them
     * to display kills.
     *
     * @param matches
     */
    getMetaData(matches) {
        const metaData$ = matches.map(result => {
            this.store.dispatch(new LoadFortniteMatchMetaDataOnceAction(result.id));
            return this.store.select(getFortniteMatchMetaData(result.id)).pipe(filter(exists), filter(data => Boolean(data.entry || (data.loading && !data.entry))));
        });
        return combineLatest(metaData$).pipe(map$1(metaData => {
            const reducedMetaData = metaData.reduce((all, current) => (current.entry ? [...all, current.entry] : all), []);
            return this.groupService.combineGroupMetaData(reducedMetaData);
        }));
    }
}
FortniteMatchMetadataService.ɵfac = function FortniteMatchMetadataService_Factory(t) { return new (t || FortniteMatchMetadataService)(i0.ɵɵinject(FortniteGroupService), i0.ɵɵinject(i2$1.Store)); };
FortniteMatchMetadataService.ɵprov = i0.ɵɵdefineInjectable({ token: FortniteMatchMetadataService, factory: FortniteMatchMetadataService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FortniteMatchMetadataService, [{
        type: Injectable
    }], function () { return [{ type: FortniteGroupService }, { type: i2$1.Store }]; }, null); })();

class FortniteRankingMemberTextService extends RankingMemberTextService {
    constructor() {
        super(...arguments);
        this.integration = "fortnite";
    }
    /**
     * Adds the kills of the match metadata to the appendix
     *
     * @param contestant
     * @param metaData
     */
    getRankingMember(contestant, metaData) {
        let text;
        const kills = metaData.contestants.reduce((killCount, con) => {
            const player = find(con.players, p => p.id === contestant.id);
            if (player) {
                return player.kills;
            }
            return killCount;
        }, null);
        if (kills === null) {
            text = this.translateService.translate("ffa-ranking:member-kills-na", {});
        }
        else {
            text = this.translateService.translate("ffa-ranking:member-kills", {
                kills: kills,
            });
        }
        return Object.assign(Object.assign({}, contestant), { appendix: text });
    }
}
FortniteRankingMemberTextService.ɵfac = function FortniteRankingMemberTextService_Factory(t) { return ɵFortniteRankingMemberTextService_BaseFactory(t || FortniteRankingMemberTextService); };
FortniteRankingMemberTextService.ɵprov = i0.ɵɵdefineInjectable({ token: FortniteRankingMemberTextService, factory: FortniteRankingMemberTextService.ɵfac });
const ɵFortniteRankingMemberTextService_BaseFactory = /*@__PURE__*/ i0.ɵɵgetInheritedFactory(FortniteRankingMemberTextService);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FortniteRankingMemberTextService, [{
        type: Injectable
    }], null, null); })();

class FortniteFFARankingModule {
}
FortniteFFARankingModule.ɵfac = function FortniteFFARankingModule_Factory(t) { return new (t || FortniteFFARankingModule)(); };
FortniteFFARankingModule.ɵmod = i0.ɵɵdefineNgModule({ type: FortniteFFARankingModule });
FortniteFFARankingModule.ɵinj = i0.ɵɵdefineInjector({ providers: [
        FortniteGroupService,
        FortniteMatchMetadataService,
        FortniteRankingMemberTextService,
        {
            provide: MATCH_METADATA,
            useExisting: FortniteMatchMetadataService,
            multi: true,
        },
        {
            provide: RANKING_MEMBER_TEXTS,
            useExisting: FortniteRankingMemberTextService,
            multi: true,
        },
    ], imports: [[CommonModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(FortniteFFARankingModule, { imports: [CommonModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FortniteFFARankingModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule],
                providers: [
                    FortniteGroupService,
                    FortniteMatchMetadataService,
                    FortniteRankingMemberTextService,
                    {
                        provide: MATCH_METADATA,
                        useExisting: FortniteMatchMetadataService,
                        multi: true,
                    },
                    {
                        provide: RANKING_MEMBER_TEXTS,
                        useExisting: FortniteRankingMemberTextService,
                        multi: true,
                    },
                ],
            }]
    }], null, null); })();

class PubgmGroupService {
    /**
     * Combines the meta data of multiple matches
     * Resulting meta data should not be used to display the rank since this will be incorrect
     *
     * @param matchMetaData
     */
    combineGroupMetaData(matchMetaData) {
        const contestantGroups = matchMetaData.reduce((data, meta) => {
            meta.contestants.forEach(contestant => {
                if (!data[contestant.id]) {
                    data[contestant.id] = Object.assign(Object.assign({}, contestant), { players: [] });
                }
                data[contestant.id].players = [...data[contestant.id].players, ...contestant.players];
            });
            return data;
        }, {});
        const contestantArray = map(contestantGroups, group => group);
        return contestantArray.reduce((result, contestant) => {
            result.contestants.push(this.combinePubgmPlayers(contestant));
            return result;
        }, { contestants: [] });
    }
    /**
     * Calculates duplicate players in to one object
     *
     * @param contestant
     */
    combinePubgmPlayers(contestant) {
        return contestant.players.reduce((data, player) => {
            const index = findIndex(data.players, p => p.id === player.id);
            if (index === -1) {
                return Object.assign(Object.assign({}, data), { players: [...data.players, player] });
            }
            data.players[index] = Object.assign(Object.assign({}, data.players[index]), { kills: data.players[index].kills + player.kills });
            return data;
        }, Object.assign(Object.assign({}, contestant), { players: [] }));
    }
}
PubgmGroupService.ɵfac = function PubgmGroupService_Factory(t) { return new (t || PubgmGroupService)(); };
PubgmGroupService.ɵprov = i0.ɵɵdefineInjectable({ token: PubgmGroupService, factory: PubgmGroupService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PubgmGroupService, [{
        type: Injectable
    }], null, null); })();

class PubgmMatchMetadataService extends MatchMetadataService {
    constructor(groupService, store) {
        super();
        this.groupService = groupService;
        this.store = store;
        this.integration = "pubgmobile";
    }
    /**
     * Fetch the metadata of all the matches in a group and combine them as 1 so we can use them
     * to display kills.
     *
     * @param matches
     */
    getMetaData(matches) {
        const metaData$ = matches.map(result => {
            this.store.dispatch(new LoadPubgmMatchMetaDataOnceAction(result.id));
            return this.store.select(getPubgmMatchMetaData(result.id)).pipe(filter(exists));
        });
        return combineLatest(metaData$).pipe(map$1(metaData => {
            const reducedMetaData = metaData.reduce((all, current) => (current.entry ? [...all, current.entry] : all), []);
            return this.groupService.combineGroupMetaData(reducedMetaData);
        }));
    }
}
PubgmMatchMetadataService.ɵfac = function PubgmMatchMetadataService_Factory(t) { return new (t || PubgmMatchMetadataService)(i0.ɵɵinject(PubgmGroupService), i0.ɵɵinject(i2$1.Store)); };
PubgmMatchMetadataService.ɵprov = i0.ɵɵdefineInjectable({ token: PubgmMatchMetadataService, factory: PubgmMatchMetadataService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PubgmMatchMetadataService, [{
        type: Injectable
    }], function () { return [{ type: PubgmGroupService }, { type: i2$1.Store }]; }, null); })();

class PubgmRankingMemberTextService extends RankingMemberTextService {
    constructor() {
        super(...arguments);
        this.integration = "pubgmobile";
    }
    /**
     * Adds the kills of the match metadata to the appendix
     *
     * @param contestant
     * @param metaData
     */
    getRankingMember(contestant, metaData) {
        let text;
        const kills = metaData.contestants.reduce((killCount, con) => {
            const player = find(con.players, p => p.id === contestant.id);
            if (player) {
                return player.kills;
            }
            return killCount;
        }, null);
        if (kills === null) {
            text = this.translateService.translate("ffa-ranking:member-kills-na", {});
        }
        else {
            text = this.translateService.translate("ffa-ranking:member-kills", {
                kills: kills,
            });
        }
        return Object.assign(Object.assign({}, contestant), { appendix: text });
    }
}
PubgmRankingMemberTextService.ɵfac = function PubgmRankingMemberTextService_Factory(t) { return ɵPubgmRankingMemberTextService_BaseFactory(t || PubgmRankingMemberTextService); };
PubgmRankingMemberTextService.ɵprov = i0.ɵɵdefineInjectable({ token: PubgmRankingMemberTextService, factory: PubgmRankingMemberTextService.ɵfac });
const ɵPubgmRankingMemberTextService_BaseFactory = /*@__PURE__*/ i0.ɵɵgetInheritedFactory(PubgmRankingMemberTextService);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PubgmRankingMemberTextService, [{
        type: Injectable
    }], null, null); })();

class PubgmFFARankingModule {
}
PubgmFFARankingModule.ɵfac = function PubgmFFARankingModule_Factory(t) { return new (t || PubgmFFARankingModule)(); };
PubgmFFARankingModule.ɵmod = i0.ɵɵdefineNgModule({ type: PubgmFFARankingModule });
PubgmFFARankingModule.ɵinj = i0.ɵɵdefineInjector({ providers: [
        PubgmGroupService,
        PubgmMatchMetadataService,
        PubgmRankingMemberTextService,
        {
            provide: MATCH_METADATA,
            useExisting: PubgmMatchMetadataService,
            multi: true,
        },
        {
            provide: RANKING_MEMBER_TEXTS,
            useExisting: PubgmRankingMemberTextService,
            multi: true,
        },
    ], imports: [[CommonModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(PubgmFFARankingModule, { imports: [CommonModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PubgmFFARankingModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule],
                providers: [
                    PubgmGroupService,
                    PubgmMatchMetadataService,
                    PubgmRankingMemberTextService,
                    {
                        provide: MATCH_METADATA,
                        useExisting: PubgmMatchMetadataService,
                        multi: true,
                    },
                    {
                        provide: RANKING_MEMBER_TEXTS,
                        useExisting: PubgmRankingMemberTextService,
                        multi: true,
                    },
                ],
            }]
    }], null, null); })();

class FFArankingModule {
}
FFArankingModule.ɵfac = function FFArankingModule_Factory(t) { return new (t || FFArankingModule)(); };
FFArankingModule.ɵmod = i0.ɵɵdefineNgModule({ type: FFArankingModule });
FFArankingModule.ɵinj = i0.ɵɵdefineInjector({ providers: [
        RankingRoundService,
        RankingGroupService,
        RankingContestantService,
        MatchMetadataService,
        RankingMemberTextService,
        {
            provide: MATCH_METADATA,
            useExisting: MatchMetadataService,
            multi: true,
        },
        {
            provide: RANKING_MEMBER_TEXTS,
            useExisting: RankingMemberTextService,
            multi: true,
        },
    ], imports: [[
            CommonModule,
            CommonsLoadingModule,
            CommonsComponentModule,
            CommonsDateModule,
            StateModule,
            TranslationModule,
            RankingComponentsModule,
            PubgFFARankingModule,
            PubgmFFARankingModule,
            FortniteFFARankingModule,
            RankingModule,
            SvgIconsModule,
            CommonsModule,
        ]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(FFArankingModule, { declarations: [RankingFfaPickerComponent,
        FfaRankingComponent,
        RankingFFAGroupSwitchComponent,
        FfaRankingSkeletonComponent,
        RankingFfaGroupSwitchSkeletonComponent,
        FfaRankingEntryComponent,
        RankingFFAOverallSwitchComponent,
        RankingFfaOverallSwitchSkeletonComponent], imports: [CommonModule,
        CommonsLoadingModule,
        CommonsComponentModule,
        CommonsDateModule,
        StateModule,
        TranslationModule,
        RankingComponentsModule,
        PubgFFARankingModule,
        PubgmFFARankingModule,
        FortniteFFARankingModule,
        RankingModule,
        SvgIconsModule,
        CommonsModule], exports: [RankingFfaPickerComponent,
        FfaRankingComponent,
        RankingFFAGroupSwitchComponent,
        FfaRankingSkeletonComponent,
        RankingFfaGroupSwitchSkeletonComponent,
        FfaRankingEntryComponent,
        RankingFFAOverallSwitchComponent,
        RankingFfaOverallSwitchSkeletonComponent] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FFArankingModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    CommonsLoadingModule,
                    CommonsComponentModule,
                    CommonsDateModule,
                    StateModule,
                    TranslationModule,
                    RankingComponentsModule,
                    PubgFFARankingModule,
                    PubgmFFARankingModule,
                    FortniteFFARankingModule,
                    RankingModule,
                    SvgIconsModule,
                    CommonsModule,
                ],
                declarations: [
                    RankingFfaPickerComponent,
                    FfaRankingComponent,
                    RankingFFAGroupSwitchComponent,
                    FfaRankingSkeletonComponent,
                    RankingFfaGroupSwitchSkeletonComponent,
                    FfaRankingEntryComponent,
                    RankingFFAOverallSwitchComponent,
                    RankingFfaOverallSwitchSkeletonComponent,
                ],
                exports: [
                    RankingFfaPickerComponent,
                    FfaRankingComponent,
                    RankingFFAGroupSwitchComponent,
                    FfaRankingSkeletonComponent,
                    RankingFfaGroupSwitchSkeletonComponent,
                    FfaRankingEntryComponent,
                    RankingFFAOverallSwitchComponent,
                    RankingFfaOverallSwitchSkeletonComponent,
                ],
                providers: [
                    RankingRoundService,
                    RankingGroupService,
                    RankingContestantService,
                    MatchMetadataService,
                    RankingMemberTextService,
                    {
                        provide: MATCH_METADATA,
                        useExisting: MatchMetadataService,
                        multi: true,
                    },
                    {
                        provide: RANKING_MEMBER_TEXTS,
                        useExisting: RankingMemberTextService,
                        multi: true,
                    },
                ],
            }]
    }], null, null); })();

/**
 * Generated bundle index. Do not edit.
 */

export { FFArankingModule, FfaRankingComponent, FfaRankingEntryComponent, FfaRankingSkeletonComponent, MATCH_METADATA, MatchMetadataService, PubgFFARankingModule, PubgGroupService, PubgRankingMemberTextService, RANKING_MEMBER_TEXTS, RankingContestantService, RankingFFAGroupSwitchComponent, RankingFFAOverallSwitchComponent, RankingFfaGroupSwitchSkeletonComponent, RankingFfaOverallSwitchSkeletonComponent, RankingFfaPickerComponent, RankingGroupService, RankingMemberTextService, RankingRoundService, ffaRankingTexts, rankingFFAGroupSwitchTexts, rankingFFAPickerTexts };
//# sourceMappingURL=play-ranking-ffa.js.map
